import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VehicleAPI, extractError } from "@/lib/api";
import { FUEL_TYPES, TRANSMISSIONS } from "@/lib/india";
import { toast } from "sonner";

const NOW = new Date().getFullYear();

export default function VehicleEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        VehicleAPI.get(id).then((v) => setForm({
            ...v,
            variant: v.variant || "",
            color: v.color || "",
            vin: v.vin || "",
            engine_number: v.engine_number || "",
            chassis_number: v.chassis_number || "",
            transmission: v.transmission || "manual",
        })).catch((e) => { toast.error(extractError(e)); navigate("/vehicles"); });
        // eslint-disable-next-line
    }, [id]);

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function save() {
        setBusy(true);
        try {
            const body = {
                make: form.make,
                model: form.model,
                variant: form.variant || null,
                year: Number(form.year),
                fuel_type: form.fuel_type,
                transmission: form.transmission,
                color: form.color || null,
                vin: form.vin || null,
                engine_number: form.engine_number || null,
                chassis_number: form.chassis_number || null,
                odometer_current: Number(form.odometer_current ?? 0),
                version: form.version,
            };
            const upd = await VehicleAPI.update(id, body);
            toast.success("Vehicle updated");
            navigate(`/vehicles/${upd.id}`);
        } catch (e) {
            const code = e?.response?.data?.error?.code;
            if (code === "OPTIMISTIC_LOCK") {
                toast.error("This record was modified elsewhere. Reloading…");
                const fresh = await VehicleAPI.get(id);
                setForm({ ...form, ...fresh });
            } else {
                toast.error(extractError(e));
            }
        } finally { setBusy(false); }
    }

    if (!form) return <div className="text-sm text-zinc-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</div>;

    return (
        <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="vehicle-edit-page">
            <header className="flex items-center justify-between">
                <div>
                    <div className="plate font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{form.registration_number}</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">Edit vehicle</h1>
                </div>
                <Link to={`/vehicles/${id}`} className="text-sm text-zinc-500 hover:underline">← Back</Link>
            </header>

            <Card className="p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Make"><Input value={form.make} onChange={(e) => set("make", e.target.value)} data-testid="vehicle-make" /></Field>
                    <Field label="Model"><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="vehicle-model" /></Field>
                    <Field label="Variant"><Input value={form.variant} onChange={(e) => set("variant", e.target.value)} /></Field>
                    <Field label="Year"><Input type="number" min={1980} max={NOW + 1} value={form.year} onChange={(e) => set("year", e.target.value)} className="font-mono tnum" /></Field>
                    <Field label="Fuel">
                        <select value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                            {FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </Field>
                    <Field label="Transmission">
                        <select value={form.transmission} onChange={(e) => set("transmission", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                            {TRANSMISSIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>
                    <Field label="Color"><Input value={form.color} onChange={(e) => set("color", e.target.value)} /></Field>
                    <Field label="VIN"><Input value={form.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())} maxLength={17} className="font-mono uppercase" /></Field>
                    <Field label="Engine no."><Input value={form.engine_number} onChange={(e) => set("engine_number", e.target.value)} className="font-mono" /></Field>
                    <Field label="Chassis no."><Input value={form.chassis_number} onChange={(e) => set("chassis_number", e.target.value)} className="font-mono" /></Field>
                    <Field label="Odometer (current)"><Input type="number" min={0} value={form.odometer_current} onChange={(e) => set("odometer_current", e.target.value)} className="font-mono tnum" /></Field>
                </div>
            </Card>

            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => navigate(`/vehicles/${id}`)}>Cancel</Button>
                <Button onClick={save} disabled={busy} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="vehicle-save">
                    {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save changes
                </Button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}
